import { type DocumentNode } from 'graphql'

import { submitConsentRecord } from '@/modules/iubenda/consentDatabase'
import { getConsentSubject } from '@/modules/iubenda/getConsentSubject'
import {
  type LegalConsentFieldKey,
  legalConsentsFields,
  type ProofTextContext,
} from '@/modules/iubenda/legalConsentsFields'
import UPDATE_USER_NOTIFICATION_SETTINGS from '@/modules/settings/profileSettings/queries/UpdateUserNotificationSettings.gql'
import UPDATE_LICENSE_AGREEMENT from '@/queries/UpdateLicenseAgreement.gql'

export type LegalConsentsValues = Record<LegalConsentFieldKey, boolean>

type Mutate = (options: {
  mutation: DocumentNode
  variables: Record<string, unknown>
}) => Promise<unknown>

export type PersistLegalConsentsArgs = {
  /** `client.mutate` (Composition API) or `this.$apollo.mutate` (Options API) — same call shape. */
  mutate: Mutate
  companyId?: string | null
  values: LegalConsentsValues
  requiresAgb: boolean
  requiresGuideline: boolean
  /**
   * US onboarding records `authorizeSendOnBehalf` instead of `acceptPrivacy`
   * (the privacy policy is already accepted with the signup consents).
   */
  isUsOnboarding?: boolean
  proofTextContext: ProofTextContext
  /**
   * Whether this call should also run the AGB/license-agreement mutation.
   * Default true. Pass false when the caller already ran that mutation
   * itself (with its own, possibly blocking, error handling) — `requiresAgb`
   * is then used ONLY to decide whether `acceptAgb` belongs in the iubenda
   * consent record, independent of who performed the mutation.
   */
  persistAgbAcceptance?: boolean
  /**
   * Whether this call should also submit the iubenda Consent Database
   * record. Default true. Pass false when an earlier step in the same
   * session already submitted one for these same answers (e.g. the AI
   * chat wizard's legal-consents step, before the review step re-runs
   * this function) — avoids a duplicate consent record for one onboarding.
   */
  submitIubendaRecord?: boolean
}

/**
 * Persists everything collected on the onboarding legal-consents step: the
 * AGB/license mutation, the two marketing-notification mutations, and one
 * bundled iubenda Consent Database record. Every step is independently
 * non-blocking — a failure anywhere is logged and never stops onboarding.
 */
export async function persistLegalConsents({
  mutate,
  companyId,
  values,
  requiresAgb,
  requiresGuideline,
  isUsOnboarding = false,
  proofTextContext,
  persistAgbAcceptance = true,
  submitIubendaRecord = true,
}: PersistLegalConsentsArgs): Promise<void> {
  if (persistAgbAcceptance && requiresAgb && values.acceptAgb && companyId) {
    try {
      await mutate({ mutation: UPDATE_LICENSE_AGREEMENT, variables: { companyId } })
    } catch (err) {
      console.error('[legalConsents] Failed to accept license agreement:', err)
    }
  }

  try {
    await Promise.all([
      mutate({
        mutation: UPDATE_USER_NOTIFICATION_SETTINGS,
        variables: {
          input: { notification: 'news', source: 'email', enabled: values.contactViaEmail },
        },
      }),
      mutate({
        mutation: UPDATE_USER_NOTIFICATION_SETTINGS,
        variables: {
          input: { notification: 'news', source: 'whatsapp', enabled: values.contactViaWhatsapp },
        },
      }),
    ])
  } catch (err) {
    console.error('[legalConsents] Failed to save marketing consent settings:', err)
  }

  if (submitIubendaRecord) {
    const subject = getConsentSubject()
    if (!subject) {
      console.warn('[legalConsents] No auth subject available, skipping iubenda consent submit')
    } else {
      try {
        const relevantFields = legalConsentsFields.filter(
          (f) =>
            (f.key !== 'acceptAgb' || requiresAgb) &&
            (f.key !== 'acceptGuideline' || (requiresGuideline && !isUsOnboarding)) &&
            (f.key !== 'acceptPrivacy' || !isUsOnboarding) &&
            (f.key !== 'authorizeSendOnBehalf' || isUsOnboarding)
        )
        const preferences = Object.fromEntries(relevantFields.map((f) => [f.key, !!values[f.key]]))
        submitConsentRecord({
          subject,
          preferences,
          proofs: {
            form: relevantFields.map((f) => f.resolveProofText(proofTextContext)).join('\n'),
            content: JSON.stringify(preferences),
          },
          legalNotices: relevantFields.flatMap((f) =>
            f.legalNoticeIdentifier ? [{ identifier: f.legalNoticeIdentifier }] : []
          ),
        })
      } catch (err) {
        console.error('[legalConsents] Failed to submit iubenda consent record:', err)
      }
    }
  }
}
